// Analiza umów jako zadanie w tle + odpytywanie (karta ASYNC-4, 20.10.2026) — WSPÓLNE dla obu UI
// (frontend/src/pages/ContractAnalysisPage.jsx i frontend/src/ui-cockpit/views/ContractView.jsx). Strony nie są ruszane:
// przełączenie robi `lib/api.js`: `analyzeContract`, `analyzeContractFile`, `analyzeContractBatch` zachowały nazwy
// i kształt wyniku (`{ data }` jak z axiosa), ale przy włączonej fladze serwera idą trasą `/async`.
//
// Rdzeń odpytywania to ten sam co w Kreatorze i poradach (`generacjaAsync.js`): błąd sieci/bramy/5xx w trakcie
// odpytywania = pytaj dalej (NIGDY nie startuj drugi raz), 410 (wynik już odebrany) i przekroczenie czasu klienta =
// `ECONNABORTED` (klasyczna strona analizy umów robi wtedy salvage z historii analiz: `trySalvageContract`; analiza jest
// zapisywana w `contract_analyses`), błąd zadania = błąd w kształcie axios (`err.response.data.detail` tekstem).
// Różnice względem porad (`poradaAsync.js`):
//   * flaga to `umowy_async` z `GET /flags` (osobna od `porada_async` i `kreator_async`),
//   * komunikaty o konflikcie dotyczą analizy umowy,
//   * odpytywanie co 4 s (analiza trwa ok. 2–5 min).
// Start jest idempotentny PO STRONIE SERWERA (skrót danych): ponowny start z tymi samymi danymi dołącza do trwającej
// analizy (202 + `dolaczono`), więc klient może bezpiecznie ponowić start po błędzie bramy (do 2 razy); 409 oznacza
// trwającą analizę Z INNYMI danymi → czytelny komunikat, formularz nietknięty.
import { czekajNaZadanie, czyBladBramy } from "./generacjaAsync";

export const INTERWAL_UMOWY_MS = 4000;
export const LICZBA_PONOWIEN_STARTU = 2;
export const PAUZA_PONOWIENIA_MS = 3000;

let pamiecFlagi = null;

/** Czy analiza umów idzie trasami async (flaga z `GET /flags`). Błąd odczytu = false (klasycznie) i nie jest zapamiętywany. */
export async function czyUmowyAsync(pobierzFlagi) {
  if (pamiecFlagi !== null) return pamiecFlagi;
  try {
    const res = await pobierzFlagi();
    pamiecFlagi = res?.data?.umowy_async === true;
    return pamiecFlagi;
  } catch {
    return false;
  }
}

/** Do testów i do wymuszenia ponownego odczytu flagi. */
export function ustawPamiecFlagiUmowyAsync(wartosc) {
  pamiecFlagi = wartosc;
}

const uspij = (ms) => new Promise((r) => setTimeout(r, ms));

/** 409 (trwa już analiza umowy z innymi danymi): czytelny komunikat zamiast obiektu `detail`, formularz zostaje. */
function bladKonfliktu(err) {
  const r = err.response;
  const szczegol = r?.data?.detail;
  const komunikat = (typeof szczegol === "string" ? szczegol : szczegol?.message)
    || "Analiza umowy już trwa. Poczekaj na jej zakończenie.";
  err.response = { ...r, data: { detail: `${komunikat} Twoje dane nie zostały usunięte.` } };
  return err;
}

/**
 * Start zadania; zwraca job_id albo `null`, gdy trasa async jest niedostępna (404 „Not Found” = flaga wyłączona po
 * stronie serwera). Błąd bramy/sieci przy starcie: ponów (serwer dołączy do już utworzonego zadania, jeśli poprzedni
 * start jednak je utworzył). Timeout klienta przy starcie (np. duży upload) NIE jest ponawiany.
 */
async function uruchom(start, opcje) {
  const ponowienia = opcje.ponowienia ?? LICZBA_PONOWIEN_STARTU;
  const pauza = opcje.pauzaPonowieniaMs ?? PAUZA_PONOWIENIA_MS;
  const spij = opcje.uspij ?? uspij;
  for (let proba = 0; ; proba++) {
    let res;
    try {
      res = await start();
    } catch (err) {
      const r = err?.response;
      if (r?.status === 404 && r?.data?.detail === "Not Found") return null; // flaga wyłączona po stronie serwera
      if (r?.status === 409) throw bladKonfliktu(err);
      if (czyBladBramy(err) && err?.code !== "ECONNABORTED" && proba < ponowienia) {
        await spij(pauza);
        continue;
      }
      throw err;
    }
    // Brak job_id w poprawnej odpowiedzi to błąd serwera, nie bramy: poza try, więc NIE jest ponawiany.
    const jobId = res?.data?.job_id;
    if (!jobId) throw new Error("Serwer nie zwrócił identyfikatora zadania analizy umowy.");
    return jobId;
  }
}

/**
 * Analiza umowy: gdy `asyncWlaczone` — jako zadanie w tle z odpytywaniem; inaczej (albo gdy trasa async jest
 * niedostępna) `klasycznie()`. Zwraca `{ data }` jak klasyczna trasa. `start()` → odpowiedź 202 z `job_id`;
 * `odczytaj(jobId)` → odpowiedź GET statusu.
 */
export async function umowyAuto({ asyncWlaczone, start, odczytaj, klasycznie, ...opcje }) {
  if (!asyncWlaczone) return klasycznie();
  const jobId = await uruchom(start, opcje);
  if (jobId === null) {
    pamiecFlagi = false;
    return klasycznie();
  }
  return czekajNaZadanie(() => odczytaj(jobId), { interwalMs: INTERWAL_UMOWY_MS, ...opcje });
}
